import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Images, Heart, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Set a project's main (placeholder) image from three sources:
 *  (a) upload from device        -> POST /upload/persistent (same path Project Info uses)
 *  (b) the project's gallery     -> GET  /projects/{id}/images
 *  (c) client-shared inspiration -> GET  /projects/{id}/portal-access
 *                                   then GET /drawing-room/{token}/gallery
 * All writes go through the EXISTING main-image endpoint:
 *      POST /projects/{id}/placeholder-image
 * Staff-only surface — never rendered in client portal views.
 */
export const SetProjectImageDialog = ({ open, onOpenChange, project, onSet, field = 'placeholder_image' }) => {
  const [uploading, setUploading] = useState(false);
  const [galleryImages, setGalleryImages] = useState(null);   // null = loading
  const [inspirations, setInspirations] = useState(null);
  const projectId = project?.id;

  const loadSources = useCallback(async () => {
    if (!projectId) return;
    setGalleryImages(null);
    setInspirations(null);
    // (b) project gallery
    axios.get(`${API}/projects/${projectId}/images`)
      .then(res => setGalleryImages(res.data.images || []))
      .catch(() => setGalleryImages([]));
    // (c) client-shared inspirations via the project's Drawing Room access
    try {
      const access = await axios.get(`${API}/projects/${projectId}/portal-access`);
      const token = access.data?.access_token || access.data?.access?.access_token;
      if (token) {
        const gal = await axios.get(`${API}/drawing-room/${token}/gallery`);
        setInspirations((gal.data.gallery || []).filter(i => i.url || i.image_url));
      } else {
        setInspirations([]);
      }
    } catch (e) {
      setInspirations([]); // no portal access yet — section shows empty note
    }
  }, [projectId]);

  useEffect(() => {
    if (open) loadSources();
  }, [open, loadSources]);

  const setMainImage = async (imageUrl, thumbnailUrl) => {
    try {
      // DP83 — `field="primary_image"` writes the chosen primary image instead
      // of the older placeholder field. Neither write touches the other.
      const endpoint = field === 'primary_image' ? 'primary-image' : 'placeholder-image';
      await axios.post(`${API}/projects/${projectId}/${endpoint}`, {
        image_url: imageUrl,
        thumbnail_url: thumbnailUrl || imageUrl,
      });
      toast.success(field === 'primary_image' ? 'Primary image set' : 'Project photo set');
      if (onSet) onSet(imageUrl, thumbnailUrl || imageUrl);
    } catch (error) {
      toast.error('Failed to set project photo');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload/persistent`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const imageUrl = uploadRes.data.url;
      // mirror Project Info's flow: set main + add to the gallery
      await axios.post(`${API}/projects/${projectId}/images`, {
        url: imageUrl,
        thumbnail_url: imageUrl,
        category: 'general',
        source: 'upload',
      }).catch(() => {});
      await setMainImage(imageUrl, imageUrl);
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const renderGrid = (items, getUrl, getThumb, emptyText, testPrefix) => {
    if (items === null) {
      return (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      );
    }
    if (items.length === 0) {
      return <p className="text-sm text-muted-foreground text-center py-10">{emptyText}</p>;
    }
    return (
      <div className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
        {items.map((item, idx) => (
          <button
            key={item.id || idx}
            type="button"
            onClick={() => setMainImage(getUrl(item), getThumb(item))}
            className="relative aspect-square rounded-md overflow-hidden border border-border hover:ring-2 hover:ring-[#BC5D41] transition-all"
            data-testid={`${testPrefix}-${idx}`}
          >
            <img
              src={getThumb(item) || getUrl(item)}
              alt={item.title || ''}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="set-project-image-dialog">
        <DialogHeader>
          <DialogTitle>Add a project photo</DialogTitle>
          <DialogDescription>
            Choose the main image for {project?.name || 'this project'}.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="upload" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="upload" className="text-xs gap-1" data-testid="image-source-upload">
              <Upload className="w-3.5 h-3.5" /> Upload
            </TabsTrigger>
            <TabsTrigger value="gallery" className="text-xs gap-1" data-testid="image-source-gallery">
              <Images className="w-3.5 h-3.5" /> Gallery
            </TabsTrigger>
            <TabsTrigger value="inspiration" className="text-xs gap-1" data-testid="image-source-inspiration">
              <Heart className="w-3.5 h-3.5" /> Client photos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="pt-4">
            <label
              className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg py-10 cursor-pointer hover:border-[#BC5D41]/50 transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
              data-testid="image-upload-dropzone"
            >
              {uploading
                ? <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                : <Upload className="w-6 h-6 text-muted-foreground" />}
              <span className="text-sm text-muted-foreground">
                {uploading ? 'Uploading…' : 'Click to choose a photo from your device'}
              </span>
              <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
          </TabsContent>

          <TabsContent value="gallery" className="pt-4">
            {renderGrid(
              galleryImages,
              (i) => i.url,
              (i) => i.thumbnail_url,
              'No photos in this project\u2019s gallery yet.',
              'gallery-image'
            )}
          </TabsContent>

          <TabsContent value="inspiration" className="pt-4">
            {renderGrid(
              inspirations,
              (i) => i.url || i.image_url,
              (i) => i.image_url || i.url,
              'No client-shared inspiration photos yet.',
              'inspiration-image'
            )}
          </TabsContent>
        </Tabs>

        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SetProjectImageDialog;
